import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link, useNavigate } from 'react-router-dom';
import {
    FaBoxOpen, FaChevronRight, FaQuestionCircle, FaRegHeart, FaSignOutAlt, FaUserCog,
} from 'react-icons/fa';
import { toast } from 'react-toastify';
import Meta from '../components/Meta';
import { logout } from '../slices/authSlice';
import { resetCart } from '../slices/cartSlice';
import { useGetMyOrdersQuery } from '../slices/ordersApiSlice';
import { useLogoutMutation, useWishlistQuery } from '../slices/usersApiSlice';

const orderStatus = (order) => {
    if (order.cancelledAt || order.preOrderStatus === 'cancelled') return 'Cancelled';
    if (order.isDelivered) return 'Delivered';
    if (order.isPreOrder) return 'Pre-order';
    return order.isPaid ? 'Processing' : 'Awaiting payment';
};

const ProfileScreen = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const { userInfo } = useSelector((state) => state.auth);

    const { data: wishlist } = useWishlistQuery(userInfo?._id, { skip: !userInfo });
    const { data: orders } = useGetMyOrdersQuery();
    const [logoutApiCall] = useLogoutMutation();

    const latestOrder = orders?.length
        ? [...orders].sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))[0]
        : null;

    const links = [
        { to: '/orders', Icon: FaBoxOpen, label: 'My Orders', hint: orders ? `${orders.length} order${orders.length === 1 ? '' : 's'}` : 'Track and view orders' },
        { to: '/wishlist', Icon: FaRegHeart, label: 'My Wishlist', hint: wishlist ? `${wishlist.length} saved item${wishlist.length === 1 ? '' : 's'}` : 'Your saved items' },
        { to: '/profile/details', Icon: FaUserCog, label: 'Account Details', hint: 'Name, email and password' },
        { to: '/help', Icon: FaQuestionCircle, label: 'Help', hint: 'Delivery, returns and contact' },
    ];

    const handleLogout = async () => {
        try {
            await logoutApiCall().unwrap();
            dispatch(logout());
            dispatch(resetCart());
            navigate('/login');
        } catch (err) {
            toast.error(`Couldn't sign you out. Please try again.`);
        }
    };

    return (
        <div className="account-page">
            <Meta title="My Account" />
            <h1 className="account-title">Hi {userInfo?.name?.split(' ')[0]}</h1>
            <p className="account-subtitle">{userInfo?.email}</p>

            {latestOrder && (
                <Link to={`/order/${latestOrder._id}`} className="account-order">
                    <div>
                        <span className="account-order__label">Latest order</span>
                        <strong>{new Date(latestOrder.createdAt).toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' })}</strong>
                        <span className="account-order__meta">£{Number(latestOrder.totalPrice).toFixed(2)} · {orderStatus(latestOrder)}</span>
                    </div>
                    <FaChevronRight aria-hidden="true" />
                </Link>
            )}

            <nav className="account-links" aria-label="Account">
                {links.map(({ to, Icon, label, hint }) => (
                    <Link key={to} to={to} className="account-link">
                        <Icon aria-hidden="true" className="account-link__icon" />
                        <span className="account-link__text">
                            <strong>{label}</strong>
                            <small>{hint}</small>
                        </span>
                        <FaChevronRight aria-hidden="true" className="account-link__chevron" />
                    </Link>
                ))}
            </nav>

            <button type="button" className="account-signout" onClick={handleLogout}>
                <FaSignOutAlt aria-hidden="true" /> Sign Out
            </button>
        </div>
    );
};

export default ProfileScreen;
